import Decimal from "decimal.js";
import { orderBy } from "lodash";

import { Holding } from "@fartherfinance/frontend/api/Investment/hooks/useGetProposalPostTradeHoldings";
import useSecuritiesBySecurityIds from "@fartherfinance/frontend/api/SecurityMaster/hooks/useSecuritiesBySecurityIds";

import displayTitleFromSecurity from "@src/utils/displayTitleFromSecurity";
import toPercent from "@src/utils/toPercent";
import { arrayToCsv } from "@src/yellowstone/modules/shared";

type SecurityDb = Awaited<
  ReturnType<typeof useSecuritiesBySecurityIds>
>["data"];

interface Props {
  securityDb: SecurityDb | undefined;
  postTradeHoldings: Holding[] | undefined;
}

const HEADER = [
  "Account",
  "Ticker",
  "Pre-Qty",
  "Post-Qty",
  "Pre-Wt",
  "Post-Wt",
  "Port-Wt",
  "Port-Dlt",
  "Pre-Mkt",
  "Post-Mkt",
  "Price",
] as const satisfies string[];

const preparePostTradesCsvData = (props: Props) => {
  if (props.postTradeHoldings === undefined || props.securityDb === undefined) {
    return [];
  }

  const isCash = props.securityDb.find((x) =>
    x.identifiers.some(
      (y) => y.registrar === "TICKER" && y.identifier === "US DOLLARS"
    )
  );

  // let's order the data by ticker and have cash at the top
  const sortedHoldings = orderBy(
    props.postTradeHoldings,

    (holding) => {
      if (holding.securityId === isCash?.id) {
        return "AAA";
      }

      const foundSec = props.securityDb?.find(
        (x) => x.id === holding.securityId
      );
      return foundSec ? displayTitleFromSecurity(foundSec) : "ZZZ";
    },

    "asc"
  );

  const csvData = sortedHoldings.map((holding) => {
    const foundSec = props.securityDb?.find((x) => x.id === holding.securityId);

    const securityName =
      holding.securityId === isCash?.id
        ? "US DOLLARS"
        : displayTitleFromSecurity(foundSec);

    return [
      holding.custodianAccountId, // Account
      securityName, // Ticker
      holding.preQuantity.toNumber(), // Pre-Qty
      holding.postQuantity.toNumber(), // Post-Qty
      toPercent(holding.preWeight.toNumber()), // Pre-Wt
      toPercent(holding.postWeight.toNumber()), // Post-Wt
      toPercent(holding.portfolioWeight.toNumber()), // Port-Wt
      toPercent(holding.portfolioWeightDelta.toNumber()), // Port-Dlt
      holding.preMarketValue.toNumber(), // Pre-Mkt
      holding.postMarketValue.toNumber(), // Post-Mkt
      holding.asOfPrice.toNumber(), // Price
    ];
  });

  return csvData;
};

const prepareTotalsCsvData = (props: Props): (string | number)[] => {
  if (props.postTradeHoldings === undefined) {
    return [];
  }

  const totals = props.postTradeHoldings.reduce(
    (acc, row) => {
      return {
        preQuantity: acc.preQuantity.plus(row.preQuantity),
        postQuantity: acc.postQuantity.plus(row.postQuantity),
        preMarketValue: acc.preMarketValue.plus(row.preMarketValue),
        postMarketValue: acc.postMarketValue.plus(row.postMarketValue),
        preWeight: acc.preWeight.plus(row.preWeight),
        postWeight: acc.postWeight.plus(row.postWeight),
        portfolioWeight: acc.portfolioWeight.plus(row.portfolioWeight),
      };
    },
    {
      preQuantity: new Decimal(0),
      postQuantity: new Decimal(0),
      preMarketValue: new Decimal(0),
      postMarketValue: new Decimal(0),
      preWeight: new Decimal(0),
      postWeight: new Decimal(0),
      portfolioWeight: new Decimal(0),
    }
  );

  return [
    "Totals", // Account
    "-", // Ticker
    "-", // Pre-Qty
    "-", // Post-Qty
    toPercent(totals.preWeight.toNumber()), // Pre-Wt
    toPercent(totals.postWeight.toNumber()), // Post-Wt
    toPercent(totals.portfolioWeight.toNumber()), // Port-Wt
    "-", // Port-Dlt
    totals.preMarketValue.toNumber(), // Pre-Mkt
    totals.postMarketValue.toNumber(), // Post-Mkt
    "-", // Price
  ];
};

const constructPostTradeCSVData = (props: Props) => {
  const data = preparePostTradesCsvData(props);
  const totalsData = prepareTotalsCsvData(props);
  const mergedData = [...data, totalsData];

  const finalCsv = arrayToCsv([HEADER, ...(mergedData ?? [])]);
  return finalCsv;
};

export default constructPostTradeCSVData;
